<template>
  <div class="project-detail">
    <!-- Loading state -->
    <div v-if="isLoading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Caricamento...</span>
      </div>
    </div>

    <!-- Error display -->
    <div v-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Success message -->
    <div v-if="successMessage" class="alert alert-success" role="alert">
      {{ successMessage }}
    </div>

    <!-- Header progetto -->
    <div v-if="project && !isLoading">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/projects">Progetti</router-link>
              </li>
              <li class="breadcrumb-item active">{{ project.project_name }}</li>
            </ol>
          </nav>
          <h2>Budget Annuali - {{ project.project_name }}</h2>
          <p class="text-muted">Budget Totale: € {{ formatCurrency(project.budget) }}</p>
          <p class="text-muted">Budget Allocato: € {{ formatCurrency(allocated_budget) }}</p>
        </div>
        <div>
          <button 
            class="btn btn-outline-secondary me-2"
            @click="showProjectEdit = true"
            title="Modifica progetto"
          >
            <i class="fas fa-edit me-1"></i>
            Modifica Progetto
          </button>
          <button 
            class="btn btn-primary"
            @click="openNewBudgetDialog"
            title="Aggiungi budget per un nuovo anno"
          >
            Nuovo Budget Anno
          </button>
        </div>
      </div>
    </div>

    <!-- DbmngForm per modifica progetto -->
    <div v-if="showProjectEdit && project" class="card mb-4">
      <div class="card-header">
        <h5 class="card-title mb-0">
          <i class="fas fa-edit me-2"></i>Modifica Progetto
        </h5>
      </div>
      <div class="card-body">
        <DbmngForm
          :entity="project"
          :api-endpoint="`/project/project/${projectId}`"
          :http-method="'PUT'"
          @success="onProjectUpdateSuccess"
          @error="onProjectUpdateError"
          @cancel="showProjectEdit = false"
        />
      </div>
    </div>


    <!-- Layout a due colonne: Allocazione (1/3) + Budget Persone (2/3) -->
    <div class="row" v-if="!isLoading">
      <!-- COLONNA SINISTRA: Allocazione dell'Anno (1/3) -->
      <div class="col-md-4">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">
              <i class="fas fa-calendar-alt me-2"></i>Allocazione dell'Anno
            </h5>
            <button 
              class="btn btn-sm btn-primary"
              @click="openNewBudgetDialog"
              title="Aggiungi budget per un nuovo anno"
            >
              <i class="fas fa-plus me-1"></i>Aggiungi
            </button>
          </div>
          <div class="card-body">
            <div v-if="budgetYears.length === 0" class="text-muted text-center py-4">
              <div class="mb-3">
                <i class="fas fa-chart-line fa-3x text-muted"></i>
              </div>
              <p class="small">Nessun budget annuale trovato.</p>
              <p class="small">Clicca su "Aggiungi" per iniziare.</p>
            </div>
            
            <div v-else>
              <!-- Tabella Budget Annuali compatta -->
              <div class="table-responsive">
                <table class="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th class="text-center">
                        <i class="fas fa-calendar me-1"></i>Anno
                      </th>
                      <th class="text-end">
                        <i class="fas fa-euro-sign me-1"></i>Budget
                      </th>
                      <th class="text-end">
                        <i class="fas fa-euro-sign me-1"></i>% di allocazione
                      </th>
                      <th class="text-center">
                        <i class="fas fa-tools me-1"></i>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="budget in sortedBudgetYears" :key="budget.year">
                      <!-- Anno -->
                      <td class="text-center">
                        <span class="badge bg-primary">
                          {{ budget.year }}
                        </span>
                      </td>
                      
                      <!-- Budget -->
                      <td class="text-end">
                        <strong class="text-success">
                          € {{ formatCurrency(budget.budget) }}
                        </strong>
                        <br>
                        <div class="small">
                          <span v-if="budget.budget_sum > budget.budget" class="text-extreme-danger">
                            € {{ formatCurrency(budget.budget_sum) }}
                          </span>
                          <span v-if="budget.budget_sum < budget.budget" class="text-danger">
                            € {{ formatCurrency(budget.budget_sum) }}
                          </span>
                          <span v-if="budget.budget_sum === budget.budget" class="text-success">
                            € {{ formatCurrency(budget.budget_sum) }}
                          </span>
                        </div>
                      </td>
                      
                      <!-- % di allocazione -->
                      <td class="text-end">
                        <strong class="text-success">
                          {{ getPercentage(budget.budget, project.budget) }}%
                        </strong>
                      </td>
                      
                      <!-- Azioni -->
                      <td class="text-center">
                        <div class="btn-group btn-group-sm" role="group">
                          <button 
                            class="btn btn-outline-primary"
                            @click="openEditBudgetDialog(budget)"
                            title="Modifica"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button 
                            class="btn btn-outline-danger"
                            @click="deleteBudget(budget.id_project, budget.year)"
                            title="Elimina"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="table-secondary">
                      <td class="text-center fw-bold">
                        <i class="fas fa-calculator me-1"></i>TOT
                      </td>
                      <td class="text-end fw-bold">
                        <strong class="text-primary">
                          € {{ formatCurrency(allocated_budget) }}
                        </strong>
                      </td>
                      <td class="text-end">
                        {{ getPercentage(allocated_budget, project.budget) }}%
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- COLONNA DESTRA: Budget per Persona/Risorsa (2/3) -->
      <div class="col-md-8">
        <div v-if="project?.budget_details?.length > 0" class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">
              <i class="fas fa-users me-2"></i>Budget per Persona/Risorsa
            </h5>
            <button 
              class="btn btn-sm btn-primary"
              @click="openNewPersonBudgetDialog"
              title="Aggiungi budget per persona"
            >
              <i class="fas fa-plus me-1"></i>Aggiungi
            </button>
          </div>
          <div class="card-body">
            <TableSortable 
              v-if="project.budget_details && project.budget_details.length > 0"
              :data="project.budget_details" 
              :options="budgetDetailsTableOptions"
            >
              <template #field-person_name="{ row }">
                <div class="d-flex align-items-center">
                  <div class="avatar-circle me-2">
                    <i class="fas fa-user"></i>
                  </div>
                  <div>
                    <router-link :to="`/people/${row.id_people}`" class="text-decoration-none">
                      <strong>{{ row.person_name || 'N/A' }}</strong>
                    </router-link>
                    <small class="text-muted d-block">ID: {{ row.id_people || 'Non assegnato' }}</small>
                  </div>
                </div>
              </template>
              
              <template #field-budget="{ row }">
                <span class="fs-6 fw-bold text-success">
                  € {{ formatCurrency(row.budget) }}
                </span>
              </template>
              
              <template #field-year="{ row }">
                <span class="badge bg-secondary">
                  {{ row.year || 'N/A' }}
                </span>
              </template>
              
              <template #field-tipo_label="{ row }">
                <span class="badge" :class="{
                  'bg-secondary': row.tipo === 0,
                  'bg-warning': row.tipo === 10,
                  'bg-success': row.tipo === 20
                }">
                  {{ row.tipo_label }}
                </span>
              </template>
              
              <template #field-note="{ row }">
                <small class="text-muted">
                  <div v-if="row.note">
                    <i class="fas fa-sticky-note me-1"></i>
                    {{ row.note }}
                  </div>
                  <div>
                    <i class="fas fa-clock me-1"></i>
                    {{ formatDate(row.updated_timestamp) }}
                  </div>
                </small>
              </template>
              
              <template #field-actions="{ row }">
                <div class="btn-group btn-group-sm">
                  <button 
                    class="btn btn-outline-primary"
                    @click="openEditPersonBudgetDialog(row)"
                    title="Modifica"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    class="btn btn-outline-danger"
                    @click="deletePersonBudget(row.id_budget)"
                    title="Elimina"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </template>
              
              <template #footer>
                <tr class="table-secondary">
                  <td class="fw-bold">
                    <i class="fas fa-calculator me-2"></i>TOTALE BUDGET PERSONE
                  </td>
                  <td class="text-end fw-bold">
                    <span class="fs-5 text-primary">
                      € {{ formatCurrency(totalBudgetDetails) }}
                    </span>
                  </td>
                  <td colspan="4" class="text-muted">
                    <small>
                      Disponibile: 
                      <span :class="getAvailableBudgetClass(totalBudgetDetails, project.budget)">
                        € {{ formatCurrency(project.budget - totalBudgetDetails) }}
                      </span>
                    </small>
                  </td>
                </tr>
              </template>
            </TableSortable>
          </div>
        </div>
        
        <!-- Messaggio se non ci sono budget persone -->
        <div v-else class="card h-100">
          <div class="card-body d-flex align-items-center justify-content-center">
            <div class="text-center text-muted">
              <i class="fas fa-users fa-3x mb-3"></i>
              <p>Nessun budget per persona/risorsa disponibile.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog Modal per Budget Anno -->
    <div 
      v-if="showDialog" 
      class="modal fade show" 
      style="display: block; background-color: rgba(0,0,0,0.5);"
      tabindex="-1" 
      role="dialog"
      @click="handleModalClick"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingBudget ? `Modifica Budget ${editingBudget.year}` : 'Nuovo Budget Anno' }}
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeDialog"
              aria-label="Chiudi"
            ></button>
          </div>
          
          <div class="modal-body">
            <!-- Error nel dialog -->
            <div v-if="dialogError" class="alert alert-danger">
              {{ dialogError }}
            </div>
            
            <!-- Form dinamico da schema DBMng -->
            <form @submit.prevent="saveBudget">
              <!-- Anno (solo per nuovo budget) -->
              <div v-if="!editingBudget" class="mb-3">
                <label for="dialog_year" class="form-label">Anno *</label>
                <input 
                  id="dialog_year"
                  v-model.number="formData.year"
                  type="number"
                  min="2020"
                  :max="new Date().getFullYear() + 5"
                  class="form-control"
                  required
                  placeholder="es. 2025"
                >
              </div>

              <!-- Budget annuale (campo principale) -->
              <div class="mb-3">
                <label for="dialog_budget" class="form-label">
                  Budget Annuale *
                  <span v-if="project" class="text-muted">
                    (Base: € {{ formatCurrency(project.budget) }})
                  </span>
                </label>
                <input
                  id="dialog_budget"
                  v-model.number="formData.budget"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  class="form-control"
                  placeholder="es. 50000.00"
                >
                <div v-if="formData.budget && project" class="form-text">
                  {{ getBudgetComparison(formData.budget, project.budget) }}
                </div>
              </div>

              <!-- Campi dinamici aggiuntivi da DBMng (se presenti) -->
              <div 
                v-for="(field, fieldName) in otherDbmngFields" 
                :key="fieldName"
                class="mb-3"
              >
                <label :for="'dialog_' + fieldName" class="form-label">
                  {{ field.label }}
                  <span v-if="!field.nullable" class="text-danger">*</span>
                </label>
                
                <input
                  :id="'dialog_' + fieldName"
                  v-model="formData[fieldName]"
                  :type="field.validator === 'email' ? 'email' : 'text'"
                  :required="!field.nullable"
                  class="form-control"
                  :placeholder="`Inserisci ${field.label.toLowerCase()}`"
                >
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeDialog"
              :disabled="isDialogLoading"
            >
              Annulla
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveBudget"
              :disabled="isDialogLoading"
            >
              <span v-if="isDialogLoading" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingBudget ? 'Salva Modifiche' : 'Crea Budget' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Budget Dialog per Persone -->
    <BudgetDialog
      v-model="showBudgetDialog"
      :budget="editingBudget"
      :defaults="budgetDefaults"
      :dbmng-schema="dbmngSchemaBudget"
      @saved="onBudgetSaved"
    />
  </div>
</template>

<script>
import projectYearAPI from '@/services/projectYearAPI.js'
import DbmngForm from '@/lib/DbmngForm.vue'
import BudgetDialog from '@/components/BudgetDialog.vue'
import budgetAPI from '@/services/budgetAPI.js'
import TableSortable from '@/components/TableSortable.vue'

export default {
  name: 'ProjectDetail',
  components: {
    DbmngForm,
    BudgetDialog,
    TableSortable
  },
  data() {
    return {
      project: null,
      budgetYears: [],
      dbmngSchema: null,
      formData: {},
      editingBudget: null,
      isLoading: false,
      error: null,
      successMessage: null,
      // Dialog state
      showDialog: false,
      isDialogLoading: false,
      dialogError: null,
      // Project edit state
      showProjectEdit: false,
      
      // Budget Dialog state
      showBudgetDialog: false,
      editingBudget: null,
      budgetDefaults: {},
      dbmngSchemaBudget: null
    }
  },
  
  computed: {

    allocated_budget() {
      return this.budgetYears.reduce((sum, budget) => {
        return sum + (parseFloat(budget.budget) || 0)
      }, 0)
    },
    
    sortedBudgetYears() {
      return [...this.budgetYears].sort((a, b) => a.year - b.year)
    },
    
    projectId() {
      return parseInt(this.$route.params.id)
    },
    
    otherDbmngFields() {
      // Filtra solo i campi che non sono 'budget' (gestito separatamente)
      if (!this.dbmngSchema?.fields) return {}
      
      const filtered = {}
      Object.keys(this.dbmngSchema.fields).forEach(fieldName => {
        if (fieldName !== 'budget') {
          filtered[fieldName] = this.dbmngSchema.fields[fieldName]
        }
      })
      return filtered
    },

    totalBudgetDetails() {
      if (!this.project?.budget_details) return 0
      return this.project.budget_details.reduce((sum, detail) => {
        return sum + (parseFloat(detail.budget) || 0)
      }, 0)
    },
    
    budgetDetailsTableOptions() {
      return {
        structure: [
          { key: 'person_name', label: 'Persona', sortable: true, filterable: true },
          { key: 'budget', label: 'Budget', sortable: true, align: 'right' },
          { key: 'year', label: 'Anno', sortable: true, align: 'center' },
          { key: 'tipo_label', label: 'Tipo', sortable: true, align: 'center' },
          { key: 'note', label: 'Dettagli', sortable: false },
          { key: 'actions', label: 'Azioni', sortable: false, align: 'center' }
        ]
      }
    }
  },
  
  async mounted() {
    await this.loadProjectAndBudgets()
  },
  
  methods: {
    async loadProjectAndBudgets() {
      try {
        this.isLoading = true
        this.error = null
        
        // Carica dati progetto
        const projectResult = await projectYearAPI.getProject(this.projectId)
        if (!projectResult.ok || !projectResult.data) {
          throw new Error('Progetto non trovato')
        }
        this.project = projectResult.data[0]
        this.project.budget_details = projectResult.budget_details || []
        
        // Carica budget anni + schema DBMng
        const budgetResult = await projectYearAPI.getAllYears(this.projectId)
        
        if (budgetResult.ok) {
          this.budgetYears = budgetResult.data || []
          this.dbmngSchema = budgetResult.dbmng
          this.initializeFormData()
        } else {
          this.error = budgetResult.message || 'Errore nel caricamento dei budget'
        }
        
        // Carica schema per budget persone se non presente
        if (!this.dbmngSchemaBudget) {
          const budgetSchemaRes = await budgetAPI.getAll()
          if (budgetSchemaRes.dbmng) {
            this.dbmngSchemaBudget = budgetSchemaRes.dbmng
          }
        }
      } catch (error) {
        this.error = error.message || 'Errore di connessione'
        console.error('Errore loadProjectAndBudgets:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    initializeFormData() {
      this.formData = { 
        year: new Date().getFullYear(),
        budget: this.project?.budget || 0
      }
      
      if (this.dbmngSchema?.fields) {
        Object.keys(this.dbmngSchema.fields).forEach(fieldName => {
          if (fieldName !== 'budget') { // Budget gestito separatamente
            this.formData[fieldName] = ''
          }
        })
      }
    },
    
    async saveBudget() {
      try {
        this.isDialogLoading = true
        this.dialogError = null
        this.error = null
        this.successMessage = null
        
        const budgetData = {
          budget: this.formData.budget
        }
        
        // Aggiungi altri campi DBMng se presenti
        Object.keys(this.otherDbmngFields).forEach(fieldName => {
          budgetData[fieldName] = this.formData[fieldName]
        })
        
        let result
        
        if (this.editingBudget) {
          // Update esistente
          result = await projectYearAPI.update(
            this.projectId, 
            this.editingBudget.year, 
            budgetData
          )
          this.successMessage = `Budget ${this.editingBudget.year} aggiornato con successo`
        } else {
          // Nuovo budget
          result = await projectYearAPI.create(
            this.projectId, 
            this.formData.year, 
            budgetData
          )
          this.successMessage = `Budget ${this.formData.year} creato con successo`
        }
        
        if (result.ok) {
          this.closeDialog()
          this.loadProjectAndBudgets() // Ricarica la lista
        } else {
          this.dialogError = result.message || 'Errore nel salvataggio'
        }
      } catch (error) {
        this.dialogError = error.message || 'Errore di connessione'
        console.error('Errore saveBudget:', error)
      } finally {
        this.isDialogLoading = false
      }
    },
    
    openNewBudgetDialog() {
      this.editingBudget = null
      this.initializeFormData()
      this.dialogError = null
      this.showDialog = true
    },
    
    openEditBudgetDialog(budget) {
      this.editingBudget = budget
      
      // Popola il form con i dati del budget
      this.formData = { 
        year: budget.year,
        budget: budget.budget || 0
      }
      
      // Aggiungi altri campi se presenti
      Object.keys(this.otherDbmngFields).forEach(fieldName => {
        this.formData[fieldName] = budget[fieldName] || ''
      })
      
      this.dialogError = null
      this.showDialog = true
    },
    
    closeDialog() {
      this.showDialog = false
      this.editingBudget = null
      this.dialogError = null
      this.isDialogLoading = false
      this.initializeFormData()
    },
    
    handleModalClick(event) {
      // Modale non si chiude quando si clicca fuori
      // if (event.target === event.currentTarget) {
      //   this.closeDialog()
      // }
    },
    
    async deleteBudget(projectId, year) {
      if (!confirm(`Sei sicuro di voler eliminare il budget per l'anno ${year}?`)) {
        return
      }
      
      try {
        this.isLoading = true
        this.error = null
        this.successMessage = null
        
        const result = await projectYearAPI.delete(projectId, year)
        
        if (result.ok) {
          this.successMessage = `Budget ${year} eliminato con successo`
          this.loadProjectAndBudgets() // Ricarica la lista
        } else {
          this.error = result.message || 'Errore nell\'eliminazione'
        }
      } catch (error) {
        this.error = error.message || 'Errore di connessione'
        console.error('Errore deleteBudget:', error)
      } finally {
        this.isLoading = false
      }
    },
    
    formatCurrency(value) {
      if (!value) return '0,00'
      return parseFloat(value).toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        return new Date(dateString).toLocaleString('it-IT')
      } catch (error) {
        return 'Data non valida'
      }
    },
    
    getBudgetComparison(yearBudget, baseBudget) {


      let available_budget = parseFloat(baseBudget) - this.allocated_budget;
      
      if (!yearBudget || !baseBudget) return 'N/A'
      
      return `
        Disponibile € ${this.formatCurrency(available_budget)}`
    },
    
    getBudgetComparisonClass(yearBudget, baseBudget) {
      if (!yearBudget || !baseBudget) return 'text-muted'
      
      const diff = parseFloat(yearBudget) - parseFloat(baseBudget)
      
      if (diff > 0) {
        return 'text-success'
      } else if (diff < 0) {
        return 'text-warning'
      } else {
        return 'text-info'
      }
    },

    /**
     * Calcola percentuale di un budget rispetto al totale
     */
    getPercentage(amount, total) {
      if (!amount || !total || total === 0) return 0
      return Math.round((parseFloat(amount) / parseFloat(total)) * 100)
    },

    /**
     * Restituisce classe CSS per badge percentuale singolo anno
     */
    getPercentageBadgeClass(amount, total) {
      const percentage = this.getPercentage(amount, total)
      if (percentage <= 25) return 'bg-info'
      if (percentage <= 50) return 'bg-success'
      if (percentage <= 75) return 'bg-warning'
      return 'bg-danger'
    },

    /**
     * Restituisce classe CSS per badge percentuale totale
     */
    getTotalPercentageBadgeClass(allocated, total) {
      const percentage = this.getPercentage(allocated, total)
      if (percentage < 75) return 'bg-success'
      if (percentage < 90) return 'bg-warning'
      if (percentage <= 100) return 'bg-danger'
      return 'bg-dark'  // Superamento budget
    },

    /**
     * Restituisce classe CSS per budget disponibile
     */
    getAvailableBudgetClass(allocated, total) {
      const available = parseFloat(total) - parseFloat(allocated)
      if (available > 0) return 'text-success fw-bold'
      if (available === 0) return 'text-warning fw-bold'
      return 'text-danger fw-bold'  // Budget superato
    },
    
    // --- Gestione Budget Persone ---
    
    openNewPersonBudgetDialog() {
      this.editingBudget = null
      this.budgetDefaults = {
        id_project: this.project.id_project,
        year: new Date().getFullYear()
      }
      this.showBudgetDialog = true
    },
    
    openEditPersonBudgetDialog(budget) {
      this.editingBudget = budget
      this.budgetDefaults = {}
      this.showBudgetDialog = true
    },
    
    async deletePersonBudget(budgetId) {
      if (!confirm('Sei sicuro di voler eliminare questo budget?')) {
        return
      }
      
      try {
        const result = await budgetAPI.delete(budgetId)
        if (result.ok) {
          this.successMessage = 'Budget eliminato con successo'
          this.loadProjectAndBudgets()
          setTimeout(() => { this.successMessage = null }, 3000)
        } else {
          this.error = result.message
        }
      } catch (e) {
        this.error = e.message
      }
    },
    
    onBudgetSaved() {
      this.successMessage = 'Budget salvato con successo'
      this.loadProjectAndBudgets()
      setTimeout(() => { this.successMessage = null }, 3000)
    },
    
    onProjectUpdateSuccess(result) {
      this.successMessage = 'Progetto aggiornato con successo'
      this.showProjectEdit = false
      this.loadProjectAndBudgets() // Ricarica i dati aggiornati
    },

    onProjectUpdateError(error) {
      this.error = error.message || 'Errore durante l\'aggiornamento del progetto'
    }
  }
}
</script>

<style scoped>
.project-detail {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.breadcrumb {
  background-color: transparent;
  padding: 0;
  margin-bottom: 1rem;
}

.card {
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

/* Dialog Modal Styles */
.modal.show {
  animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-content {
  border: none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-header {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.modal-title {
  font-weight: 600;
  color: #495057;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  font-weight: bold;
  color: #000;
  opacity: 0.5;
  cursor: pointer;
}

.btn-close:hover {
  opacity: 0.8;
}

.btn-close:before {
  content: '×';
}

.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-info {
  color: #17a2b8 !important;
}

@media (max-width: 768px) {
  .project-detail {
    padding: 10px;
  }
  
  .col-md-6 {
    margin-bottom: 1rem;
  }
  
  /* Adattamenti tabella per mobile */
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .table th, .table td {
    padding: 0.5rem 0.25rem;
    vertical-align: middle;
  }
  
  .btn-group .btn {
    padding: 0.25rem 0.5rem;
  }
  
  /* Nascondi colonne meno importanti su mobile */
  .table th:nth-child(3),
  .table th:nth-child(4),
  .table td:nth-child(3),
  .table td:nth-child(4) {
    display: none;
  }
}

/* Stili per la tabella budget */
.table {
  margin-bottom: 0;
}

.table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
}

.table tbody tr:hover {
  background-color: #f5f5f5;
}

.table tfoot tr {
  border-top: 2px solid #dee2e6;
}

.table tfoot td {
  font-weight: 500;
  background-color: #e9ecef;
}

.badge {
  font-size: 0.875em;
}

.badge.fs-6 {
  font-size: 1rem !important;
}

/* Colori aggiuntivi per i badge */
.bg-info {
  background-color: #17a2b8 !important;
}

.bg-success {
  background-color: #28a745 !important;
}

.bg-warning {
  background-color: #ffc107 !important;
  color: #212529 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.bg-dark {
  background-color: #343a40 !important;
}

.btn-group {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-radius: 0.375rem;
}

.btn-group .btn {
  border-radius: 0;
}

.btn-group .btn:first-child {
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.btn-group .btn:last-child {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

/* Avatar circle per persone */
.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #6c757d;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
}
</style>